/** Safe, bounded diagnostics for app guards. Never serialize the thrown value. */
export type AppGuardCode =
  | 'APP_NOT_FOUND'
  | 'APP_MANIFEST_INVALID'
  | 'APP_STALE_MOUNT'
  | 'APP_BUNDLE_UNAVAILABLE';

export type AppGuardContext = {
  /** Only supply identity from a successfully authorized app bundle. */
  appNodeId?: string;
  appVersion?: string;
  source?: 'draft' | 'published' | 'unpublished';
  manifestRevision?: {
    nodeId: string;
    contentGeneration: number;
    structuralGeneration: number;
  };
  buildSha?: string;
};

const CODES: readonly AppGuardCode[] = [
  'APP_NOT_FOUND',
  'APP_MANIFEST_INVALID',
  'APP_STALE_MOUNT',
  'APP_BUNDLE_UNAVAILABLE',
];
const OPAQUE_ID = /^[a-z0-9]{24,40}$/;
const VERSION = /^(?:sha256:)?[a-f0-9]{8,64}$/;

function errorText(error: unknown): string | null {
  // Convex client errors carry the server value in data; message may be generic.
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data: unknown = error.data;
    if (typeof data === 'string') return data.slice(0, 4096);
    if (
      typeof data === 'object' &&
      data !== null &&
      'code' in data &&
      typeof data.code === 'string'
    )
      return data.code.slice(0, 4096);
  }
  if (typeof error === 'string') return error.slice(0, 4096);
  if (error instanceof Error) return error.message.slice(0, 4096);
  return null;
}

export function appGuardDiagnostic(
  error: unknown,
  context: AppGuardContext,
  operation: string,
  correlationId: string
) {
  const text = errorText(error);
  const code = CODES.find((candidate) =>
    text?.match(new RegExp(`\\b${candidate}\\b`))
  );
  if (!code) return null;
  const revision = context.manifestRevision;
  const validRevision =
    revision &&
    OPAQUE_ID.test(revision.nodeId) &&
    Number.isSafeInteger(revision.contentGeneration) &&
    revision.contentGeneration >= 0 &&
    Number.isSafeInteger(revision.structuralGeneration) &&
    revision.structuralGeneration >= 0;
  const message = error instanceof Error ? error.message.slice(0, 4096) : '';
  const requestId = (text?.match(/\[Request ID: ([a-f0-9]{16,64})\]/) ??
    message.match(/\[Request ID: ([a-f0-9]{16,64})\]/))?.[1];
  return {
    error_code: code,
    // Caller-owned method names only; malformed names never become telemetry.
    error_operation: /^[a-zA-Z][a-zA-Z0-9_.:]{0,79}$/.test(operation)
      ? operation
      : 'app_guard',
    correlation_id: /^[a-f0-9-]{16,64}$/.test(correlationId)
      ? correlationId
      : undefined,
    convex_request_id: requestId,
    app_node_id:
      context.appNodeId && OPAQUE_ID.test(context.appNodeId)
        ? context.appNodeId
        : undefined,
    app_version:
      context.appVersion &&
      (VERSION.test(context.appVersion) ||
        ['unpublished', 'published-unavailable'].includes(context.appVersion))
        ? context.appVersion
        : undefined,
    bundle_source: ['draft', 'published', 'unpublished'].includes(
      context.source ?? ''
    )
      ? context.source
      : undefined,
    manifest_node_id: validRevision ? revision.nodeId : undefined,
    manifest_content_generation: validRevision
      ? revision.contentGeneration
      : undefined,
    manifest_structural_generation: validRevision
      ? revision.structuralGeneration
      : undefined,
    build_sha:
      context.buildSha && /^[a-f0-9]{40}$/.test(context.buildSha)
        ? context.buildSha
        : undefined,
    // Fixed category, not parser text (which may contain authored content).
    validation_code:
      code === 'APP_MANIFEST_INVALID' ? 'manifest_invalid' : undefined,
  };
}

export type AppGuardReporter = (
  error: unknown,
  operation: string,
  scope?: 'unknown_target'
) => void;

/** One bounded set per authorized mount/revision; reporting never changes errors. */
export function createAppGuardReporter(
  context: AppGuardContext,
  correlationId: string,
  capture: (
    properties: NonNullable<ReturnType<typeof appGuardDiagnostic>>
  ) => void
): AppGuardReporter {
  const seen = new Set<string>();
  return (error, operation, scope) => {
    try {
      const properties = appGuardDiagnostic(
        error,
        scope === 'unknown_target' ? { buildSha: context.buildSha } : context,
        operation,
        correlationId
      );
      if (!properties) return;
      const key = `${properties.error_code}:${properties.error_operation}`;
      if (seen.has(key) || seen.size >= 32) return;
      seen.add(key);
      capture(properties);
    } catch {
      // Observability cannot alter the original rejection, response or guard.
    }
  };
}

/** Cross-app admission can fail for either app; never attach the caller as target. */
export function appGuardTargetScope(
  method: string,
  params: unknown
): 'unknown_target' | undefined {
  return method === 'apps.call' ||
    (method.startsWith('table.') &&
      typeof params === 'object' &&
      params !== null &&
      'app' in params &&
      params.app != null)
    ? 'unknown_target'
    : undefined;
}
